import { createId } from "../../app/runtimeContext";
import type { SupabaseClient } from "@supabase/supabase-js";
import type { ObservabilityClient } from "../../observability/types";
import type { Product } from "../../domain/enterprise";
import type { Database } from "../../types/database";
import type { ConversationManager } from "./conversationManager";
import type { DecisionEngine } from "./decisionEngine";
import type { GoalManager } from "./goalManager";
import type { PlanningEngine } from "./planningEngine";
import type { CyanIntelligenceRepository } from "./repositories";
import type { ReasoningPipeline } from "./reasoningPipeline";
import { RequestInterpreter } from "./requestInterpreter";
import { ContextAssemblyService } from "./contextAssemblyService";
import { ApprovalEngine } from "./approvalEngine";
import { ToolIntentService } from "./toolIntentService";
import { KnowledgeGraphService } from "./knowledgeGraphService";
import type {
  ProductContext,
  CyanLiveWorkspaceState,
  SafeReasoningSummary,
  TimelineEvent,
} from "./liveTypes";
import type { Conversation, ConversationMessage } from "./types";
import type {
  MemoryRetrievalScope,
  PersistentMemoryRepository,
} from "../memory/persistentMemoryRepository";
import type { GroundedMemoryRetrievalService } from "../memory/groundedMemoryRetrievalService";

export type CyanLiveRequest = {
  owner: string;
  message: string;
  product: Product;
  conversationId?: string;
  projectId?: string;
  correlationId?: string;
};

export class CyanLiveService {
  constructor(
    private readonly repository: CyanIntelligenceRepository,
    private readonly conversations: ConversationManager,
    private readonly goals: GoalManager,
    private readonly planning: PlanningEngine,
    private readonly decisions: DecisionEngine,
    private readonly reasoning: ReasoningPipeline,
    private readonly interpreter: RequestInterpreter,
    private readonly contextAssembly: ContextAssemblyService,
    private readonly approvals: ApprovalEngine,
    private readonly toolIntents: ToolIntentService,
    private readonly graph: KnowledgeGraphService,
    private readonly observability: ObservabilityClient,
    private readonly supabase?: SupabaseClient<Database>,
    private readonly persistentMemory?: PersistentMemoryRepository,
    private readonly groundedMemory?: GroundedMemoryRetrievalService,
  ) {}

  private conversationTitle(message: string, productName: string): string {
    const normalized = message
      .replace(/\s+/g, " ")
      .trim()
      .replace(/^[^a-zA-Z0-9À-ÿ]+/, "");

    if (!normalized) return productName;

    const firstSentence = normalized.split(/[.!?\n]/)[0]?.trim() || normalized;

    const words = firstSentence.split(/\s+/).slice(0, 8);
    const title = words.join(" ");

    return title.length > 64 ? `${title.slice(0, 61).trim()}...` : title;
  }

  private async findConversation(
    context: ProductContext,
    conversationId: string,
  ): Promise<Conversation> {
    const conversations = await this.repository.listConversations({
      productId: context.productId,
    });

    const conversation = conversations.find(
      (item) => item.id === conversationId,
    );

    if (!conversation) {
      throw new Error("Conversation not found.");
    }

    return conversation;
  }

  async renameConversation(
    context: ProductContext,
    conversationId: string,
    title: string,
  ): Promise<CyanLiveWorkspaceState> {
    const conversation = await this.findConversation(context, conversationId);

    const normalized = title.replace(/\s+/g, " ").trim();

    if (!normalized) {
      throw new Error("Conversation title cannot be empty.");
    }

    await this.repository.saveConversation({
      ...conversation,
      title: normalized.slice(0, 100),
      context: {
        ...conversation.context,
        titleEditedByOwner: true,
      },
      updatedAt: new Date().toISOString(),
    });

    return this.loadConversation(context, conversationId);
  }

  async setConversationStatus(
    context: ProductContext,
    conversationId: string,
    status: "open" | "closed" | "archived",
  ): Promise<CyanLiveWorkspaceState> {
    const conversation = await this.findConversation(context, conversationId);

    const now = new Date().toISOString();

    await this.repository.saveConversation({
      ...conversation,
      context: {
        ...conversation.context,
        status,
        closedAt: status === "closed" ? now : null,
        archivedAt: status === "archived" ? now : null,
      },
      updatedAt: now,
    });

    if (status === "archived") {
      return this.createWorkspace(context);
    }

    return this.loadConversation(context, conversationId);
  }

  async pinMessage(
    context: ProductContext,
    conversationId: string,
    messageId: string,
    title: string,
  ): Promise<CyanLiveWorkspaceState> {
    const conversation = await this.findConversation(context, conversationId);

    const messages = await this.repository.listMessages(conversationId);
    const message = messages.find((item) => item.id === messageId);

    if (!message) {
      throw new Error("Message not found.");
    }

    const rawPins = Array.isArray(conversation.context?.pins)
      ? conversation.context.pins
      : [];

    const existingPins = rawPins.filter(
      (
        value,
      ): value is {
        messageId: string;
        title: string;
        createdAt: string;
      } =>
        typeof value === "object" &&
        value !== null &&
        typeof (value as { messageId?: unknown }).messageId === "string",
    );

    const withoutCurrent = existingPins.filter(
      (pin) => pin.messageId !== messageId,
    );

    const pinTitle =
      title.replace(/\s+/g, " ").trim() ||
      message.content.replace(/\s+/g, " ").trim().slice(0, 60) ||
      "Pinned message";

    await this.repository.saveConversation({
      ...conversation,
      context: {
        ...conversation.context,
        pins: [
          ...withoutCurrent,
          {
            messageId,
            title: pinTitle.slice(0, 100),
            createdAt: new Date().toISOString(),
          },
        ],
      },
      updatedAt: new Date().toISOString(),
    });

    return this.loadConversation(context, conversationId);
  }

  async unpinMessage(
    context: ProductContext,
    conversationId: string,
    messageId: string,
  ): Promise<CyanLiveWorkspaceState> {
    const conversation = await this.findConversation(context, conversationId);

    const rawPins = Array.isArray(conversation.context?.pins)
      ? conversation.context.pins
      : [];

    const pins = rawPins.filter((value) => {
      if (typeof value !== "object" || value === null) return false;

      return (value as { messageId?: unknown }).messageId !== messageId;
    });

    await this.repository.saveConversation({
      ...conversation,
      context: {
        ...conversation.context,
        pins,
      },
      updatedAt: new Date().toISOString(),
    });

    return this.loadConversation(context, conversationId);
  }

  async summarizeAndDeleteConversation(
    context: ProductContext,
    conversationId: string,
  ): Promise<CyanLiveWorkspaceState> {
    if (!this.supabase || !this.persistentMemory) {
      throw new Error(
        "Persistent conversation summary requires authenticated memory runtime.",
      );
    }

    const conversation = await this.findConversation(context, conversationId);

    const messages = await this.repository.listMessages(conversationId);

    const { data: summaryResponse, error: summaryError } =
      await this.supabase.functions.invoke("cyan-conversation-summary", {
        body: {
          conversationId,
        },
      });

    if (summaryError) {
      throw new Error(`Conversation summary failed: ${summaryError.message}`);
    }

    const summary =
      typeof summaryResponse?.summary === "string"
        ? summaryResponse.summary.trim()
        : "";

    if (!summary) {
      throw new Error(
        "Conversation summary was empty. Conversation was not deleted.",
      );
    }

    const {
      data: { user },
    } = await this.supabase.auth.getUser();

    if (!user?.id) {
      throw new Error("Authenticated memory actor could not be resolved.");
    }

    const organizationId = await this.repository.resolveOrganizationId(
      context.organizationId,
    );

    await this.persistentMemory.save({
      organizationId,

      userId: user.id,

      projectId: context.projectId,

      memoryType: "conversation_summary",

      content: summary,

      source: {
        type: "conversation_summary_snapshot",

        id: conversationId,

        path: `deleted-conversation/${conversationId}`,
      },

      confidence: 1,

      embeddingStatus: "pending",

      metadata: {
        retainedByOwner: true,

        canonicalAuthority: false,

        originalConversationId: conversationId,

        originalConversationTitle: conversation.title,

        originalMessageCount: messages.length,

        sourceConversationDeleted: true,

        providerModel: summaryResponse?.model ?? null,

        providerUsage: summaryResponse?.usage ?? null,

        retainedAt: new Date().toISOString(),
      },
    });

    await this.repository.deleteConversation(conversationId);

    await this.observability.event({
      category: "cyan",

      eventName: "cyan_conversation_summary_retained",

      message:
        "Conversation was summarized into persistent context memory before hard deletion.",

      productId: context.productId,

      context: {
        conversationId,

        messageCount: messages.length,
      },
    });

    return this.createWorkspace(context);
  }

  async listConversationSummaries(context: ProductContext): Promise<
    Array<{
      id: string;
      content: string;
      title: string;
      originalConversationId?: string;
      createdAt: string;
    }>
  > {
    if (!this.supabase) {
      return [];
    }

    const {
      data: { user },
    } = await this.supabase.auth.getUser();

    if (!user?.id) {
      return [];
    }

    const organizationId = await this.repository.resolveOrganizationId(
      context.organizationId,
    );

    /*
     * cyan_memory_records already exists as canonical SS memory
     * storage, but the generated Supabase Database type in this
     * branch does not yet expose that table to the generic client.
     *
     * Keep the escape hatch local and structural. Do not weaken the
     * type of the canonical Supabase client across the application.
     */
    type ConversationSummaryRow = {
      id: string;
      content: string;
      metadata: Record<string, unknown> | null;
      created_at: string;
      project_id: string | null;
    };

    interface ConversationSummaryQueryResult {
      data: ConversationSummaryRow[] | null;
      error: {
        message: string;
      } | null;
    }

    interface ConversationSummaryQuery {
      eq(column: string, value: string): ConversationSummaryQuery;

      is(column: string, value: null): ConversationSummaryQuery;

      order(
        column: string,
        options: {
          ascending: boolean;
        },
      ): ConversationSummaryQuery;

      limit(count: number): Promise<ConversationSummaryQueryResult>;
    }

    interface ConversationSummaryTable {
      select(columns: string): ConversationSummaryQuery;
    }

    interface ConversationSummarySupabase {
      from(table: "cyan_memory_records"): ConversationSummaryTable;
    }

    const memorySupabase = this
      .supabase as unknown as ConversationSummarySupabase;

    let query = memorySupabase
      .from("cyan_memory_records")
      .select("id,content,metadata,created_at,project_id")
      .eq("organization_id", organizationId)
      .eq("user_id", user.id)
      .eq("memory_type", "conversation_summary");

    query = context.projectId
      ? query.eq("project_id", context.projectId)
      : query.is("project_id", null);

    const { data, error } = await query
      .order("created_at", {
        ascending: false,
      })
      .limit(100);

    if (error) {
      throw new Error(
        `Unable to load conversation summaries: ${error.message}`,
      );
    }

    return (data ?? []).map((row) => {
      const metadata = row.metadata ?? {};

      return {
        id: row.id,

        content: row.content,

        title:
          typeof metadata.originalConversationTitle === "string"
            ? metadata.originalConversationTitle
            : "Conversation summary",

        originalConversationId:
          typeof metadata.originalConversationId === "string"
            ? metadata.originalConversationId
            : undefined,

        createdAt: row.created_at,
      };
    });
  }

  async createWorkspace(
    context: ProductContext,
  ): Promise<CyanLiveWorkspaceState> {
    /*
     * Conversation hydration is the critical path.
     *
     * Auxiliary executive panels are best-effort: a stale/missing table
     * must never prevent Cyan chat from opening or restoring history.
     */
    const conversations = await this.repository.listConversations({
      productId: context.productId,
    });

    const activeConversation = conversations[0];

    let messages: ConversationMessage[] = [];
    if (activeConversation) {
      try {
        messages = await this.repository.listMessages(activeConversation.id);
      } catch {
        messages = [];
      }
    }

    const settled = await Promise.allSettled([
      this.repository.listApprovals({ productId: context.productId }),
      this.repository.listReasoningSummaries({ productId: context.productId }),
      this.repository.listContextSources({ productId: context.productId }),
      this.repository.listToolIntents({ productId: context.productId }),
      this.repository.listTimeline({ productId: context.productId }),
      this.repository.listDecisions({ productId: context.productId }),
      this.repository.listReflections({ productId: context.productId }),
    ]);

    const value = <T>(index: number): T[] =>
      settled[index]?.status === "fulfilled"
        ? (settled[index].value as T[])
        : [];

    return {
      productContext: context,
      conversations,
      activeConversation,
      messages,
      approvals: value(0),
      reasoningSummaries: value(1),
      contextSources: value(2),
      toolIntents: value(3),
      timeline: value(4),
      decisions: value(5),
      reflections: value(6),
    };
  }

  async loadConversation(
    context: ProductContext,
    conversationId: string,
  ): Promise<CyanLiveWorkspaceState> {
    const base = await this.createWorkspace(context);
    const activeConversation = base.conversations.find(
      (item) => item.id === conversationId,
    );

    if (!activeConversation) {
      return base;
    }

    let messages: ConversationMessage[] = [];
    try {
      messages = await this.repository.listMessages(conversationId);
    } catch {
      messages = [];
    }

    return {
      ...base,
      activeConversation,
      messages,
    };
  }

  async deleteConversation(
    context: ProductContext,
    conversationId: string,
  ): Promise<CyanLiveWorkspaceState> {
    await this.repository.deleteConversation(conversationId);
    return this.createWorkspace(context);
  }

  async submit(
    input: CyanLiveRequest,
    hooks?: {
      onProgress?: (stage: string, detail: string) => void;
    },
  ): Promise<CyanLiveWorkspaceState> {
    const progress = (stage: string, detail: string) => {
      hooks?.onProgress?.(stage, detail);
    };

    progress("starting", "Preparing your request");

    const correlationId = input.correlationId ?? createId("cyan_live");
    const productContext: ProductContext = {
      organizationId: input.product.organizationId,
      productId: input.product.id,
      productName: input.product.name,
      projectId: input.projectId,
    };
    let conversation = input.conversationId
      ? (
          await this.repository.listConversations({
            productId: input.product.id,
          })
        ).find((item) => item.id === input.conversationId)
      : undefined;
    conversation ??= await this.conversations.createConversation({
      organizationId: productContext.organizationId,
      productId: productContext.productId,
      title: this.conversationTitle(input.message, input.product.name),
      context: { correlationId, productName: input.product.name },
    });
    await this.repository.saveContextSelection({
      ...productContext,
      conversationId: conversation.id,
      correlationId,
      owner: input.owner,
    });
    const ownerMessage = {
      id: createId("message"),
      role: "owner" as const,
      content: input.message,
      intent: "plan" as const,
      reasoning: [],
      memoryReferences: [],
      knowledgeReferences: [],
      toolUsage: [],
      decisionReferences: [],
      createdAt: new Date().toISOString(),
    };
    let durableProgramId: string | null = null;

    /*
     * WAVE C DURABLE SEND AUTHORITY
     *
     * Production persists the owner turn and admits any operational
     * mission through one short authenticated keepalive request.
     *
     * The browser may disappear after this request is initiated.
     */
    if (this.supabase) {
      progress("mission", "Persisting message and admitting background work");

      const {
        data: { session },
        error: sessionError,
      } = await this.supabase.auth.getSession();

      if (sessionError || !session?.access_token) {
        throw new Error(
          "Authenticated Cyan session is required for durable send.",
        );
      }

      const supabaseUrl = (
        this.supabase as unknown as {
          supabaseUrl?: string;
        }
      ).supabaseUrl;

      if (!supabaseUrl) {
        throw new Error(
          "Supabase function URL is unavailable for durable send.",
        );
      }

      const durableResponse = await fetch(
        `${supabaseUrl}/functions/v1/cyan-mission-ingress`,
        {
          method: "POST",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            message: input.message,
            messageId: ownerMessage.id,
            projectId: input.projectId ?? null,
            conversationId: conversation.id,
            correlationId,
          }),
        },
      );

      const durableEnvelope = (await durableResponse
        .json()
        .catch(() => null)) as {
        persisted?: unknown;
        admitted?: unknown;
        programId?: unknown;
        error?: unknown;
      } | null;

      if (!durableResponse.ok || durableEnvelope?.persisted !== true) {
        throw new Error(
          typeof durableEnvelope?.error === "string"
            ? durableEnvelope.error
            : "Cyan durable send failed.",
        );
      }

      durableProgramId =
        typeof durableEnvelope.programId === "string"
          ? durableEnvelope.programId
          : null;

      progress(
        "mission",
        durableEnvelope.admitted === true
          ? `Background mission admitted as ${String(
              durableEnvelope.programId,
            )}`
          : "Message persisted; no operational mission required",
      );
    }

    /*
     * Idempotent repository write preserves offline/test behavior and
     * reconciles the canonical browser workspace with server truth.
     */
    await this.repository.saveMessage(conversation.id, ownerMessage);

    progress("conversation", "Saved your message to the conversation");

    /*
     * Persistent memory is derived from already-persisted authoritative
     * conversation evidence. Failure here must never lose the owner turn.
     */
    let memoryReferences: string[] = [];
    let retrievedMemoryContext: string[] = [];
    let activeMemoryScope: MemoryRetrievalScope | undefined;

    if (this.supabase && this.persistentMemory && this.groundedMemory) {
      progress("memory", "Searching conversation and persistent memory");
      try {
        const {
          data: { user },
        } = await this.supabase.auth.getUser();

        if (!user?.id) {
          throw new Error("Authenticated memory actor could not be resolved.");
        }

        const organizationId = await this.repository.resolveOrganizationId(
          productContext.organizationId,
        );

        activeMemoryScope = {
          organizationId,
          userId: user.id,
          ...(input.projectId ? { projectId: input.projectId } : {}),
        };

        await this.persistentMemory.save({
          organizationId,
          userId: user.id,
          projectId: input.projectId,
          conversationId: conversation.id,
          messageId: ownerMessage.id,
          memoryType: "conversation",
          content: ownerMessage.content,
          source: {
            type: "conversation_message",
            id: ownerMessage.id,
            path: `conversation/${conversation.id}/message/${ownerMessage.id}`,
          },
          confidence: 1,
          embeddingStatus: "pending",
          metadata: {
            role: "owner",
            derived: true,
            canonicalAuthority: false,
          },
        });

        const retrieved = await this.groundedMemory.retrieve(
          activeMemoryScope,
          input.message,
          12,
        );

        const historical = retrieved.records.filter(
          (record) => record.source.id !== ownerMessage.id,
        );

        memoryReferences = [...new Set(historical.map((record) => record.id))];

        retrievedMemoryContext = historical.map(
          (record) => `memory:${record.id}:${record.content}`,
        );
      } catch (error) {
        activeMemoryScope = undefined;
        memoryReferences = [];
        retrievedMemoryContext = [];

        await this.observability.warn({
          category: "cyan",
          eventName: "cyan_memory_owner_persist_failed",
          message: "Cyan continued without persistent memory enrichment.",
          correlationId,
          productId: productContext.productId,
          context: {
            conversationId: conversation.id,
            reason:
              error instanceof Error ? error.message : "unknown memory error",
          },
        });
      }
    }

    progress("context", "Reading project context and relevant records");

    const interpretation = this.interpreter.interpret({
      message: input.message,
      context: productContext,
      conversationId: conversation.id,
    });
    const contextSources = await this.contextAssembly.assemble({
      context: productContext,
      conversationId: conversation.id,
    });
    progress("reasoning", "Understanding your request and dependencies");

    const reasoningTrace = this.reasoning.run({
      organizationId: productContext.organizationId,
      prompt: input.message,
      context: [
        ...contextSources.map(
          (source) => `${source.sourceType}:${source.title}`,
        ),
        ...retrievedMemoryContext,
      ],
    });
    const summary: SafeReasoningSummary = {
      id: createId("reasoning_summary"),
      organizationId: productContext.organizationId,
      productId: productContext.productId,
      conversationId: conversation.id,
      interpretedIntent: interpretation.interpretedObjective,
      knownFactsUsed: [
        ...contextSources.map((source) => source.title),
        reasoningTrace.finalSummary,
      ],
      dependenciesIdentified: interpretation.affectedSystems,
      assumptions: interpretation.assumptions,
      questions: interpretation.missingInformation,
      optionsConsidered: [
        "Prepare owner-reviewed plan",
        "Ask clarification if confidence is low",
      ],
      selectedApproach: interpretation.shouldAskClarifyingQuestion
        ? "Ask clarification before executable work"
        : "Prepare plan for owner approval",
      confidence: interpretation.confidence,
      risk: interpretation.estimatedRisk,
      evidenceReferences: contextSources.map((source) => source.sourceId),
      createdAt: new Date().toISOString(),
    };
    await this.repository.saveReasoningSummary(summary);
    const goal = await this.goals.createGoal({
      organizationId: productContext.organizationId,
      productId: productContext.productId,
      owner: input.owner,
      scope: "project",
      title: interpretation.interpretedObjective,
      description: interpretation.requestedOutcome,
      milestones: [
        { title: "Interpret", weight: 1 },
        { title: "Plan", weight: 2 },
        { title: "Approve", weight: 1 },
      ],
    });
    const plan = await this.planning.createPlan({
      goal,
      objectiveTitles: ["Confirm scope", "Prepare tasks", "Request approval"],
    });
    const decision = await this.decisions.decide({
      organizationId: productContext.organizationId,
      question: `Select planning approach for ${input.product.name}`,
      options: [
        {
          id: "owner-plan",
          label: "Owner-approved plan",
          cost: 0.2,
          risk: 0.15,
          benefit: 0.9,
          confidence: 0.88,
          dependencies: 0.2,
          urgency: 0.6,
          strategicValue: 0.9,
          technicalDebt: 0.1,
        },
        {
          id: "autonomous",
          label: "Autonomous execution",
          cost: 0.4,
          risk: 1,
          benefit: 0.3,
          confidence: 0.1,
          dependencies: 0.9,
          urgency: 0.3,
          strategicValue: 0.1,
          technicalDebt: 0.9,
        },
      ],
    });
    const approval = await this.approvals.request({
      organizationId: productContext.organizationId,
      productId: productContext.productId,
      conversationId: conversation.id,
      planId: plan.id,
      category: "plan_approval",
      title: "Approve Cyan plan",
      summary: plan.summary,
      requestedBy: input.owner,
    });
    const intents = await this.toolIntents.createForPlan(plan, productContext);
    const planNode = this.graph.addNode({
      id: plan.id,
      type: "Plan",
      title: plan.summary,
      productId: productContext.productId,
      metadata: { goalId: goal.id },
    });
    const conversationNode = this.graph.addNode({
      id: conversation.id,
      type: "Conversation",
      title: conversation.title,
      productId: productContext.productId,
      metadata: {},
    });
    this.graph.relate(planNode.id, conversationNode.id, "generated_from", 0.95);
    let cyanText: string;

    if (this.supabase) {
      progress("model", "Cyan is generating the response");

      const invokeCyanChat = () =>
        this.supabase!.functions.invoke("cyan-chat", {
          body: {
            message: input.message,
            projectId: input.projectId ?? null,
            conversationId: conversation.id,
            memoryReferences,
            operationalProgramId: durableProgramId,
          },
        });

      /*
       * CYAN_CHAT_TRANSIENT_RETRY
       *
       * A single bounded retry absorbs short-lived Edge Function/network
       * failures without creating an execution loop or a duplicate mission.
       * Operational admission already occurred independently above.
       */
      let providerResult = await invokeCyanChat();

      if (providerResult.error) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        providerResult = await invokeCyanChat();
      }

      const data = providerResult.data;
      const error = providerResult.error;

      const providerEnvelope = data as {
        code?: unknown;
        error?: unknown;
        retryable?: unknown;
        resumable?: unknown;
        requiresOwnerAction?: unknown;
      } | null;

      if (providerEnvelope?.code === "AI_CREDITS_EXHAUSTED") {
        const message =
          typeof providerEnvelope.error === "string"
            ? providerEnvelope.error
            : "DeepSeek credits are exhausted. Add credits to resume Cyan execution.";

        await this.observability.error({
          category: "cyan",
          eventName: "cyan_ai_credits_exhausted",
          message,
          correlationId,
          productId: productContext.productId,
          context: {
            conversationId: conversation.id,
            provider: "deepseek",
            resumable: true,
            requiresOwnerAction: true,
          },
        });

        throw new Error(message);
      }

      if (error) {
        await this.observability.error({
          category: "cyan",
          eventName: "cyan_chat_provider_failed",
          message: "Cyan chat provider request failed after bounded retry.",
          correlationId,
          productId: productContext.productId,
          error,
          context: {
            conversationId: conversation.id,
            retryCount: 1,
            rawProviderErrorHiddenFromConversation: true,
          },
        });

        /*
         * Never expose transport/runtime diagnostics as Cyan's conversational
         * answer. Full technical evidence remains in observability.
         *
         * This fallback intentionally does not fabricate an answer to the
         * Owner's question. It truthfully reports temporary conversational
         * unavailability while durable operational work remains independent.
         */
        const portugueseSignal =
          /[áàâãéêíóôõúç]|\b(?:oi|olá|ola|por|favor|poderia|pode|quero|preciso|como|qual|quais|porque|porquê|sobre|tudo|explica|explique|me|você|voce)\b/i.test(
            input.message,
          );

        cyanText = portugueseSignal
          ? "Tive uma falha temporária ao gerar minha resposta. Sua mensagem foi preservada. Tente novamente em alguns segundos — os detalhes técnicos ficaram registrados para diagnóstico."
          : "I had a temporary problem generating my response. Your message was preserved. Please try again in a few seconds — the technical details were recorded for diagnosis.";
      } else {
        const providerText =
          typeof data?.text === "string" ? data.text.trim() : "";

        if (!providerText) {
          await this.observability.error({
            category: "cyan",
            eventName: "cyan_chat_provider_empty_response",
            message: "Cyan chat provider returned no conversational content.",
            correlationId,
            productId: productContext.productId,
            context: {
              conversationId: conversation.id,
            },
          });

          const portugueseSignal =
            /[áàâãéêíóôõúç]|\b(?:oi|olá|ola|por|favor|poderia|pode|quero|preciso|como|qual|quais|porque|porquê|sobre|tudo|explica|explique|me|você|voce)\b/i.test(
              input.message,
            );

          cyanText = portugueseSignal
            ? "Não consegui gerar uma resposta completa agora. Sua mensagem foi preservada; tente novamente em alguns segundos."
            : "I couldn't generate a complete response just now. Your message was preserved; please try again in a few seconds.";
        } else {
          cyanText = providerText;
        }
      }
    } else {
      /*
       * Tests and intentionally offline runtime only.
       * Production AuthRuntime always provides the authenticated
       * Supabase browser client.
       */
      cyanText =
        `I prepared a safe plan for ${input.product.name}. ` +
        "Approval is required before execution.";
    }

    progress("response", "Response received — saving and verifying it");

    const cyanMessage = {
      id: createId("message"),
      role: "cyan" as const,
      content: cyanText,
      intent: "plan" as const,
      reasoning: [summary.selectedApproach],
      memoryReferences,
      knowledgeReferences: summary.evidenceReferences,
      toolUsage: intents.map((intent) => intent.tool),
      decisionReferences: [decision.id],
      createdAt: new Date().toISOString(),
    };

    await this.repository.saveMessage(conversation.id, cyanMessage);

    progress("persistence", "Saving Cyan response and conversation memory");

    if (activeMemoryScope && this.persistentMemory) {
      try {
        await this.persistentMemory.save({
          organizationId: activeMemoryScope.organizationId,
          userId: activeMemoryScope.userId,
          projectId: activeMemoryScope.projectId,
          conversationId: conversation.id,
          messageId: cyanMessage.id,
          memoryType: "conversation",
          content: cyanMessage.content,
          source: {
            type: "conversation_message",
            id: cyanMessage.id,
            path: `conversation/${conversation.id}/message/${cyanMessage.id}`,
          },
          confidence: 1,
          embeddingStatus: "pending",
          metadata: {
            role: "cyan",
            derived: true,
            canonicalAuthority: false,
          },
        });
      } catch (error) {
        await this.observability.warn({
          category: "cyan",
          eventName: "cyan_memory_response_persist_failed",
          message:
            "Cyan response remained available although memory persistence failed.",
          correlationId,
          productId: productContext.productId,
          context: {
            conversationId: conversation.id,
            reason:
              error instanceof Error ? error.message : "unknown memory error",
          },
        });
      }
    }

    const timeline: TimelineEvent = {
      id: createId("timeline"),
      organizationId: productContext.organizationId,
      productId: productContext.productId,
      conversationId: conversation.id,
      planId: plan.id,
      eventType: "plan_created",
      title: plan.summary,
      status: approval.status,
      metadata: { correlationId, decisionId: decision.id },
      createdAt: new Date().toISOString(),
    };
    await this.repository.saveTimelineEvent(timeline);

    await this.repository.saveConversation({
      ...conversation,
      context: {
        ...conversation.context,
        status: "open",
        lastActivityAt: new Date().toISOString(),
      },
      updatedAt: new Date().toISOString(),
    });

    await this.observability.event({
      category: "cyan",
      eventName: "cyan_live_plan_created",
      message: "Cyan Live created a product-scoped plan and approval request.",
      correlationId,
      productId: productContext.productId,
      context: {
        conversationId: conversation.id,
        planId: plan.id,
        approvalId: approval.id,
      },
    });
    return {
      productContext,
      conversations: await this.repository.listConversations({
        productId: productContext.productId,
      }),
      activeConversation: conversation,
      messages: await this.repository.listMessages(conversation.id),
      currentGoal: goal,
      currentPlan: plan,
      approvals: await this.repository.listApprovals({
        productId: productContext.productId,
      }),
      reasoningSummaries: await this.repository.listReasoningSummaries({
        productId: productContext.productId,
      }),
      contextSources,
      toolIntents: intents,
      timeline: await this.repository.listTimeline({
        productId: productContext.productId,
      }),
      decisions: [decision],
      reflections: await this.repository.listReflections({
        productId: productContext.productId,
      }),
    };
  }
}
